import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Chip,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';

const ReminderTestConfig = ({
  campaign,
  templates,
  communicationEndpoints,
  onTest,
  testStatus,
  testError,
}) => {
  const [selectedReminderIndex, setSelectedReminderIndex] = useState(0);
  const [testData, setTestData] = useState({
    email: '',
    phone: '',
    message: '',
    twilio_number: '',
  });

  const handleTestDataChange = (field) => (event) => {
    setTestData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const getReminderTimeLabel = (reminderTime, index) => {
    if (campaign.reminder_schedule.use_relative_schedule) {
      const parts = [];
      if (reminderTime.days_before_relative > 0) {
        parts.push(`${reminderTime.days_before_relative} day(s) before`);
      }
      if (reminderTime.hours_before > 0) {
        parts.push(`${reminderTime.hours_before} hour(s) before`);
      }
      if (reminderTime.minutes_before > 0) {
        parts.push(`${reminderTime.minutes_before} minute(s) before`);
      }
      return parts.length > 0 ? parts.join(', ') : 'Immediate';
    } else {
      return `${reminderTime.days_before} day(s) before at ${reminderTime.time}`;
    }
  };

  const handleTest = () => {
    const selectedReminder = campaign.reminder_schedule.reminder_times[selectedReminderIndex];
    const message = selectedReminder.messages[0];

    if (campaign.channel === 'voice') {
      // Voice-specific validation and test data preparation
      const voiceConfig = message.voice_config;
      
      if (!voiceConfig.from_endpoint) {
        onTest({
          error: 'From endpoint is required for voice campaigns'
        });
        return;
      }

      if (!testData.phone) {
        onTest({
          error: 'Phone number is required for voice test'
        });
        return;
      }

      // For voice, we don't need message content as it's managed by the platform
      onTest({
        phone: testData.phone,
        reminderIndex: selectedReminderIndex,
        voiceConfig: voiceConfig
      });
    } else {
      // Non-voice channel validation and test data preparation
      const messageContent = message.template ? 
        templates.find(t => t.id === message.template)?.content : 
        message.content;

      if (!messageContent) {
        onTest({
          error: 'Message content is required'
        });
        return;
      }

      const fromEndpoint = message[`${campaign.channel}_config`]?.from_endpoint;
      if (!fromEndpoint) {
        onTest({
          error: 'From endpoint is required'
        });
        return;
      }

      if (campaign.channel === 'email' && !testData.email) {
        onTest({
          error: 'Email address is required for email test'
        });
        return;
      }

      if ((campaign.channel === 'sms' || campaign.channel === 'chat') && !testData.phone) {
        onTest({
          error: 'Phone number is required for SMS/Chat test'
        });
        return;
      }

      onTest({
        email: testData.email,
        phone: testData.phone,
        message: messageContent,
        twilio_number: fromEndpoint,
        reminderIndex: selectedReminderIndex
      });
    }
  };

  const getVoicePlatformInfo = () => {
    const selectedReminder = campaign.reminder_schedule.reminder_times[selectedReminderIndex];
    const message = selectedReminder.messages[0];
    const voiceConfig = message.voice_config;

    return {
      platform: voiceConfig.platform,
      voice: voiceConfig.voice_name,
      temperature: voiceConfig.temperature,
      maxDuration: voiceConfig.max_duration,
      fromEndpoint: voiceConfig.from_endpoint
    };
  };

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Typography variant="h6" gutterBottom>
        Test Reminder
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Select Reminder Time</InputLabel>
            <Select
              value={selectedReminderIndex}
              onChange={(e) => setSelectedReminderIndex(e.target.value)}
              label="Select Reminder Time"
            >
              {campaign.reminder_schedule.reminder_times.map((reminderTime, index) => (
                <MenuItem key={index} value={index}>
                  {getReminderTimeLabel(reminderTime, index)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {campaign.channel === 'email' && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Test Email Address"
              value={testData.email}
              onChange={handleTestDataChange('email')}
              type="email"
              required
            />
          </Grid>
        )}

        {(campaign.channel === 'sms' || campaign.channel === 'voice' || campaign.channel === 'chat') && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Test Phone Number"
              value={testData.phone}
              onChange={handleTestDataChange('phone')}
              type="tel"
              required
              placeholder="+1234567890"
            />
          </Grid>
        )}

        {campaign.channel === 'voice' && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Voice Configuration Details
              </Typography>
              {(() => {
                const voiceInfo = getVoicePlatformInfo();
                return (
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Platform:</strong> {voiceInfo.platform}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Voice:</strong> {voiceInfo.voice}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Temperature:</strong> {voiceInfo.temperature}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        <strong>Max Duration:</strong> {voiceInfo.maxDuration} min
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body2">
                        <strong>From Endpoint:</strong> {voiceInfo.fromEndpoint || 'Not configured'}
                      </Typography>
                    </Grid>
                  </Grid>
                );
              })()}
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            variant="contained"
            startIcon={testStatus === 'loading' ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleTest}
            disabled={testStatus === 'loading'}
            fullWidth
          >
            {testStatus === 'loading' ? 'Sending Test...' : `Send Test ${campaign.channel.charAt(0).toUpperCase() + campaign.channel.slice(1)}`}
          </Button>
        </Grid>

        {testStatus === 'success' && (
          <Grid item xs={12}>
            <Alert severity="success">
              Test {campaign.channel} sent successfully!
            </Alert>
          </Grid>
        )}

        {testStatus === 'error' && (
          <Grid item xs={12}>
            <Alert severity="error">
              {testError}
            </Alert>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

ReminderTestConfig.propTypes = {
  campaign: PropTypes.shape({
    channel: PropTypes.oneOf(['email', 'sms', 'voice', 'chat']).isRequired,
    reminder_schedule: PropTypes.shape({
      use_relative_schedule: PropTypes.bool.isRequired,
      reminder_times: PropTypes.arrayOf(
        PropTypes.shape({
          days_before: PropTypes.number,
          time: PropTypes.string,
          days_before_relative: PropTypes.number,
          hours_before: PropTypes.number,
          minutes_before: PropTypes.number,
          messages: PropTypes.arrayOf(
            PropTypes.shape({
              template: PropTypes.string,
              content: PropTypes.string,
              email_config: PropTypes.object,
              sms_config: PropTypes.object,
              voice_config: PropTypes.object,
              chat_config: PropTypes.object,
            })
          ),
        })
      ).isRequired,
    }).isRequired,
  }).isRequired,
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  communicationEndpoints: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  onTest: PropTypes.func.isRequired,
  testStatus: PropTypes.oneOf(['idle', 'loading', 'success', 'error']),
  testError: PropTypes.string,
};

ReminderTestConfig.defaultProps = {
  testStatus: 'idle',
  testError: null,
};

export default ReminderTestConfig; 